import React, { useMemo, useState } from "react";
import Select from "react-select";
import { toast, Toaster } from "react-hot-toast";
import { Search, RefreshCw, Download } from "lucide-react";
import { DateRangePicker } from "rsuite";
import "rsuite/dist/rsuite.min.css";
import subDays from "date-fns/subDays";
import addDays from "date-fns/addDays";
import startOfWeek from "date-fns/startOfWeek";
import endOfWeek from "date-fns/endOfWeek";
import startOfMonth from "date-fns/startOfMonth";
import endOfMonth from "date-fns/endOfMonth";
import addMonths from "date-fns/addMonths";
import * as XLSX from "xlsx";
import { useCurrentDomain } from "../../context/DomainContext";
import { useCompanies } from "../../hooks";
import { getMonthlyFaxReport } from "../../services/masterDataService";
import { selectStyles } from "../../helpers";
import { THEME } from "../../theme/themeColors";
import MonthlyFaxDetailsReport from "./MonthlyFaxDetailsReport";
import "./MonthlyFaxReport.css";

const predefinedBottomRanges = [
  { label: "Today", value: [new Date(), new Date()] },
  { label: "Yesterday", value: [addDays(new Date(), -1), addDays(new Date(), -1)] },
  { label: "This week", value: [startOfWeek(new Date()), endOfWeek(new Date())] },
  { label: "Last 7 days", value: [subDays(new Date(), 6), new Date()] },
  { label: "Last 30 days", value: [subDays(new Date(), 29), new Date()] },
  { label: "This month", value: [startOfMonth(new Date()), endOfMonth(new Date())] },
  { label: "Last month", value: [startOfMonth(addMonths(new Date(), -1)), endOfMonth(addMonths(new Date(), -1))] },
  { label: "This year", value: [new Date(new Date().getFullYear(), 0, 1), new Date(new Date().getFullYear(), 11, 31)] },
  { label: "Last year", value: [new Date(new Date().getFullYear() - 1, 0, 1), new Date(new Date().getFullYear() - 1, 11, 31)] },
];

const pageSizeOptions = [
  { value: 25, label: "25 / page" },
  { value: 50, label: "50 / page" },
  { value: 100, label: "100 / page" },
];

const CompanyOption = (props) => {
  const { data, isSelected, innerRef, innerProps } = props;
  return (
    <div
      ref={innerRef}
      {...innerProps}
      style={{
        display: "flex",
        alignItems: "center",
        gap: 8,
        padding: "8px 10px",
        cursor: "pointer",
        background: isSelected ? THEME.primary : props.isFocused ? "#f3f4f6" : "#fff",
        color: isSelected ? THEME.onPrimary : "#111827",
      }}
    >
      <input type="checkbox" checked={isSelected} readOnly style={{ margin: 0 }} />
      <span style={{ fontSize: 13 }}>{data.label}</span>
    </div>
  );
};

const toYmd = (d) => {
  if (!d) return "";
  const x = new Date(d);
  const mm = String(x.getMonth() + 1).padStart(2, "0");
  const dd = String(x.getDate()).padStart(2, "0");
  return `${x.getFullYear()}-${mm}-${dd}`;
};

const formatDate = (v) => {
  if (!v) return "";
  const d = new Date(v);
  if (Number.isNaN(d.getTime())) return "";
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${mm}/${dd}/${d.getFullYear()}`;
};

const MonthlyFaxReport = () => {
  const { domain } = useCurrentDomain();
  const companies = useCompanies(domain);

  const [selectedCompanies, setSelectedCompanies] = useState([]);
  const [fromDate, setFromDate] = useState(null);
  const [toDate, setToDate] = useState(null);
  const [loading, setLoading] = useState(false);
  const [rows, setRows] = useState([]);
  const [detailsCtx, setDetailsCtx] = useState(null);

  const [viewMode, setViewMode] = useState("summary");
  const [detailRows, setDetailRows] = useState([]);
  const [detailTotal, setDetailTotal] = useState(0);
  const [detailPage, setDetailPage] = useState(1);
  const [detailPageSize, setDetailPageSize] = useState(25);
  const [detailSearch, setDetailSearch] = useState("");
  const [appliedDetailSearch, setAppliedDetailSearch] = useState("");
  const [detailLoading, setDetailLoading] = useState(false);

  const companyOptions = useMemo(() => {
    const list = Array.isArray(companies)
      ? companies
      : companies?.data || companies?.companies || [];
    const mapped =
      list.length && list[0]?.value != null && list[0]?.label != null
        ? list
        : list.map((c) => ({
            value: c.szCompanyId || c.companyId || c.value || c.SZ_COMPANY_ID,
            label: `${c.szCompanyName || c.companyName || c.label || ""} (${c.szCompanyId || c.companyId || ""})`,
          }));

    const selected = new Set((selectedCompanies || []).map((c) => c.value));
    return [...mapped].sort((a, b) => {
      const aSel = selected.has(a.value) ? 0 : 1;
      const bSel = selected.has(b.value) ? 0 : 1;
      if (aSel !== bSel) return aSel - bSel;
      return String(a.label).localeCompare(String(b.label));
    });
  }, [companies, selectedCompanies]);

  const companyIdParam = (selectedCompanies || [])
    .map((c) => c?.value)
    .filter(Boolean)
    .join(",");

  const loadSummary = async () => {
    if (!domain) {
      toast.error("Domain is missing");
      return;
    }
    if (!fromDate || !toDate) {
      toast.error("Date range is required");
      return;
    }
    setLoading(true);
    try {
      const res = await getMonthlyFaxReport({
        domain,
        companyId: companyIdParam,
        fromDate: toYmd(fromDate),
        toDate: toYmd(toDate),
        tabName: "Summary",
        pageNumber: 1,
        pageSize: 25,
        isExport: false,
      });
      const data = res?.summary ? res : res?.data || {};
      const list = data?.summary || [];
      setRows(list);
      if (!list.length) toast("No data");
    } catch (err) {
      toast.error(err?.response?.data?.message || "Failed to load report");
      setRows([]);
    } finally {
      setLoading(false);
    }
  };

  const openDetails = (companyId, account, source = "") => {
    if (!fromDate || !toDate) {
      toast.error("Date range is required");
      return;
    }
    setDetailsCtx({
      domain,
      companyId,
      account: account || "",
      source: source || "",
      fromDate: toYmd(fromDate),
      toDate: toYmd(toDate),
    });
  };

  const handleReset = () => {
    setSelectedCompanies([]);
    setFromDate(null);
    setToDate(null);
    setRows([]);
    setViewMode("summary");
    setDetailRows([]);
    setDetailTotal(0);
    setDetailPage(1);
    setDetailSearch("");
    setAppliedDetailSearch("");
  };

  const handleDownload = async () => {
    if (!fromDate || !toDate) {
      toast.error("Date range is required");
      return;
    }
    setLoading(true);
    try {
      const res = await getMonthlyFaxReport({
        domain,
        companyId: companyIdParam,
        fromDate: toYmd(fromDate),
        toDate: toYmd(toDate),
        tabName: "Summary",
        pageNumber: 1,
        pageSize: 25,
        isExport: true,
      });
      const data = res?.summary ? res : res?.data || {};
      const list = data?.summary || [];
      if (!list.length) {
        toast.error("No data to download");
        return;
      }
      const mapped = list.map((r) => ({
        Account: r.account,
        CompanyId: r.companyId,
        NewBill: r.newBillCount,
        BillSearch: r.billSearchCount,
        "Total Faxes": r.totalFaxes,
      }));
      const ws = XLSX.utils.json_to_sheet(mapped);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, "Summary");
      XLSX.writeFile(wb, "MonthlyFaxReport_Summary.xlsx");
      toast.success(`Exported ${list.length} rows`);
    } catch (err) {
      toast.error(err?.response?.data?.message || "Export failed");
    } finally {
      setLoading(false);
    }
  };

  const detailTotalPages = Math.max(1, Math.ceil(detailTotal / detailPageSize));
  const safeDetailPage = Math.min(detailPage, detailTotalPages);

  const loadAllDetails = async (page, size, search = appliedDetailSearch) => {
    if (!domain) return toast.error("Domain is missing");
    if (!fromDate || !toDate) return toast.error("Date range is required");

    setDetailLoading(true);
    try {
      const res = await getMonthlyFaxReport({
        domain,
        companyId: companyIdParam || "",
        fromDate: toYmd(fromDate),
        toDate: toYmd(toDate),
        source: "",
        searchText: search || "",
        tabName: "Details",
        pageNumber: page,
        pageSize: size,
        isExport: false,
      });
      const data = res?.items ? res : res?.data || {};
      setDetailRows(data?.items || []);
      setDetailTotal(data?.totalCount || 0);
      setDetailPage(page);
      setDetailPageSize(size);
      setAppliedDetailSearch(search || "");
      if (!(data?.items || []).length) toast("No data");
    } catch (err) {
      toast.error(err?.response?.data?.message || "Failed to load details");
      setDetailRows([]);
      setDetailTotal(0);
    } finally {
      setDetailLoading(false);
    }
  };

  const handleDetailSearch = () => loadAllDetails(1, detailPageSize, detailSearch.trim());

  const mapDetailRow = (r) => ({
    Account: r.account,
    CompanyId: r.companyId,
    "Patient Name [Case#]": r.patientNameCase,
    "Created By": r.createdBy,
    "Fax# From": r.faxFrom,
    "Fax# To": r.faxTo,
    SenderName: r.senderName,
    FaxRecipientName: r.faxRecipientName,
    TotalFaxedPages: r.totalFaxedPages,
    "Faxed Date": formatDate(r.faxedDate),
    "Fax Ack Date": formatDate(r.faxAckDate),
    Source: r.source,
  });

  const handleDetailDownload = async () => {
    if (!fromDate || !toDate) return toast.error("Date range is required");
    setDetailLoading(true);
    try {
      const res = await getMonthlyFaxReport({
        domain,
        companyId: companyIdParam || "",
        fromDate: toYmd(fromDate),
        toDate: toYmd(toDate),
        source: "",
        searchText: appliedDetailSearch || "",
        tabName: "Details",
        pageNumber: 1,
        pageSize: 25,
        isExport: true,
      });
      const data = res?.items ? res : res?.data || {};
      const list = data?.items || [];
      if (!list.length) return toast.error("No data to download");
      if ((data.totalCount || 0) > list.length) {
        toast.error(`Export incomplete: ${list.length} of ${data.totalCount}`);
      }
      const mapped = list.map(mapDetailRow);
      const ws = XLSX.utils.json_to_sheet(mapped);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, "Details");
      XLSX.writeFile(wb, "MonthlyFaxReport_AllDetails.xlsx");
      toast.success(`Exported ${list.length} rows`);
    } catch (err) {
      toast.error(err?.response?.data?.message || "Export failed");
    } finally {
      setDetailLoading(false);
    }
  };

  if (detailsCtx) {
    return (
      <MonthlyFaxDetailsReport
        domain={detailsCtx.domain}
        companyId={detailsCtx.companyId}
        account={detailsCtx.account}
        source={detailsCtx.source}
        fromDate={detailsCtx.fromDate}
        toDate={detailsCtx.toDate}
        onBack={() => setDetailsCtx(null)}
      />
    );
  }

  return (
    <div className="mfr-page">
      <Toaster position="top-right" />

      <div className="mfr-filters">
        <div className="mfr-field mfr-company">
          <label>Company</label>
          <Select
            options={companyOptions}
            value={selectedCompanies}
            onChange={(opts) => setSelectedCompanies(opts || [])}
            styles={{
              ...selectStyles,
              control: (base, state) => ({
                ...(selectStyles.control ? selectStyles.control(base, state) : base),
                minHeight: 36,
                height: "auto",
                alignSelf: "center",
              }),
              valueContainer: (base) => ({
                ...base,
                flexWrap: "wrap",
                gap: 3,
                paddingTop: 3,
                paddingBottom: 3,
                paddingLeft: 6,
                paddingRight: 6,
              }),
              multiValue: (base) => ({
                ...base,
                margin: 0,
                maxWidth: "100%",
                background: THEME.alpha(0.12),
              }),
              multiValueLabel: (base) => ({
                ...base,
                whiteSpace: "normal",
                fontSize: 11,
                lineHeight: "14px",
                padding: "2px 4px",
              }),
              multiValueRemove: (base) => ({
                ...base,
                paddingLeft: 2,
                paddingRight: 2,
              }),
              indicatorsContainer: (base) => ({
                ...base,
                alignSelf: "center",
                paddingTop: 4,
              }),
            }}
            isMulti
            isClearable
            closeMenuOnSelect={false}
            hideSelectedOptions={false}
            components={{ Option: CompanyOption }}
            placeholder="All companies"
            menuPortalTarget={document.body}
            menuPosition="fixed"
          />
        </div>

        <div className="mfr-field mfr-date">
          <label>Faxed Date *</label>
          <DateRangePicker
            value={fromDate && toDate ? [fromDate, toDate] : null}
            onChange={(value) => {
              if (!value || value.length !== 2) {
                setFromDate(null);
                setToDate(null);
                return;
              }
              setFromDate(value[0]);
              setToDate(value[1]);
            }}
            ranges={predefinedBottomRanges}
            placeholder="Select date range"
            format="MM/dd/yyyy"
            size="sm"
            cleanable
            placement="bottomStart"
            preventOverflow
          />
        </div>

        <div className="mfr-actions">
          <button
            type="button"
            className="mfr-btn mfr-btn-search"
            onClick={() => {
              if (viewMode === "detailed") {
                loadAllDetails(1, detailPageSize, appliedDetailSearch);
              } else {
                loadSummary();
              }
            }}
            disabled={loading || detailLoading}
          >
            <Search size={14} />
            {loading ? "Loading..." : "Search"}
          </button>
          <button type="button" className="mfr-btn mfr-btn-reset" onClick={handleReset} disabled={loading || detailLoading}>
            <RefreshCw size={14} />
            Reset
          </button>
          {viewMode === "summary" && (
            <button
              type="button"
              className="mfr-btn mfr-btn-reset"
              onClick={handleDownload}
              disabled={loading || !rows.length}
            >
              <Download size={14} />
              Excel
            </button>
          )}
        </div>
      </div>

      <div className="mfr-toggle-row">
        <div className="mfr-toggle">
          <button
            type="button"
            className={`mfr-toggle-btn ${viewMode === "summary" ? "active" : ""}`}
            onClick={() => setViewMode("summary")}
          >
            Summary
          </button>
          <button
            type="button"
            className={`mfr-toggle-btn ${viewMode === "detailed" ? "active" : ""}`}
            onClick={() => {
              setViewMode("detailed");
              if (!fromDate || !toDate) {
                toast.error("Date range is required");
                return;
              }
              loadAllDetails(1, detailPageSize, "");
            }}
          >
            Detailed
          </button>
        </div>

        {viewMode === "detailed" && (
          <div className="mfr-detail-toolbar">
            <input
              className="mfr-input"
              value={detailSearch}
              onChange={(e) => setDetailSearch(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && handleDetailSearch()}
              placeholder="Fax From / To / Recipient / Source"
            />
            <button
              type="button"
              className="mfr-btn mfr-btn-search"
              onClick={handleDetailSearch}
              disabled={detailLoading}
            >
              <Search size={14} />
              Search
            </button>
            <button
              type="button"
              className="mfr-btn mfr-btn-reset"
              onClick={() => {
                setDetailSearch("");
                loadAllDetails(1, detailPageSize, "");
              }}
              disabled={detailLoading}
            >
              <RefreshCw size={14} />
              Reset
            </button>
            <button
              type="button"
              className="mfr-btn mfr-btn-reset"
              onClick={handleDetailDownload}
              disabled={detailLoading || !detailRows.length}
            >
              <Download size={14} />
              Excel
            </button>
          </div>
        )}
      </div>

      {viewMode === "summary" && (
        <div className="mfr-table-wrap">
          <table className="mfr-table">
            <thead>
              <tr>
                <th>Account</th>
                <th>Company Id</th>
                <th>NewBill</th>
                <th>BillSearch</th>
                <th>Total Faxes</th>
              </tr>
            </thead>
            <tbody>
              {rows.length === 0 ? (
                <tr>
                  <td colSpan={5} className="mfr-empty">
                    {loading ? "Loading..." : "Select date range and click Search"}
                  </td>
                </tr>
              ) : (
                rows.map((r) => (
                  <tr key={r.companyId}>
                    <td>
                      {r.totalFaxes > 0 ? (
                        <button type="button" className="mfr-link" onClick={() => openDetails(r.companyId, r.account, "")}>
                          {r.account}
                        </button>
                      ) : (
                        r.account
                      )}
                    </td>
                    <td>{r.companyId}</td>
                    <td>
                      {r.newBillCount > 0 ? (
                        <button type="button" className="mfr-link" onClick={() => openDetails(r.companyId, r.account, "NewBill")}>
                          {r.newBillCount}
                        </button>
                      ) : (
                        r.newBillCount
                      )}
                    </td>
                    <td>
                      {r.billSearchCount > 0 ? (
                        <button type="button" className="mfr-link" onClick={() => openDetails(r.companyId, r.account, "BillSearch")}>
                          {r.billSearchCount}
                        </button>
                      ) : (
                        r.billSearchCount
                      )}
                    </td>
                    <td>
                      {r.totalFaxes > 0 ? (
                        <button type="button" className="mfr-link" onClick={() => openDetails(r.companyId, r.account, "")}>
                          {r.totalFaxes}
                        </button>
                      ) : (
                        r.totalFaxes
                      )}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      )}

      {viewMode === "detailed" && (
        <>
          <div className="mfr-table-wrap">
            {detailLoading && <div className="mfr-loading">Loading...</div>}
            <table className="mfr-table">
              <thead>
                <tr>
                  <th>Account</th>
                  <th>Company Id</th>
                  <th>Patient Name [Case#]</th>
                  <th>Created By</th>
                  <th>Fax# From</th>
                  <th>Fax# To</th>
                  <th>Sender Name</th>
                  <th>Fax Recipient Name</th>
                  <th>Total Faxed Pages</th>
                  <th>Faxed Date</th>
                  <th>Fax Ack Date</th>
                  <th>Source</th>
                </tr>
              </thead>
              <tbody>
                {detailRows.length === 0 ? (
                  <tr>
                    <td colSpan={12} className="mfr-empty">
                      {detailLoading ? "" : "Select date range and switch to Detailed"}
                    </td>
                  </tr>
                ) : (
                  detailRows.map((r, i) => (
                    <tr key={`${r.faxFrom}|${r.faxTo}|${i}`}>
                      <td>{r.account}</td>
                      <td>{r.companyId}</td>
                      <td>{r.patientNameCase}</td>
                      <td>{r.createdBy}</td>
                      <td>{r.faxFrom}</td>
                      <td>{r.faxTo}</td>
                      <td>{r.senderName}</td>
                      <td>{r.faxRecipientName}</td>
                      <td>{r.totalFaxedPages}</td>
                      <td>{formatDate(r.faxedDate)}</td>
                      <td>{formatDate(r.faxAckDate)}</td>
                      <td>{r.source}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {detailTotal > 0 && (
            <div className="mfr-pager">
              <Select
                options={pageSizeOptions}
                value={pageSizeOptions.find((o) => o.value === detailPageSize)}
                onChange={(opt) => loadAllDetails(1, opt.value, appliedDetailSearch)}
                styles={selectStyles}
                menuPlacement="top"
                isSearchable={false}
              />
              <span>
                {(safeDetailPage - 1) * detailPageSize + 1}-
                {Math.min(safeDetailPage * detailPageSize, detailTotal)} of {detailTotal}
              </span>
              <button
                type="button"
                className="mfr-btn mfr-btn-reset"
                disabled={safeDetailPage <= 1 || detailLoading}
                onClick={() => loadAllDetails(safeDetailPage - 1, detailPageSize, appliedDetailSearch)}
              >
                Prev
              </button>
              <button
                type="button"
                className="mfr-btn mfr-btn-reset"
                disabled={safeDetailPage >= detailTotalPages || detailLoading}
                onClick={() => loadAllDetails(safeDetailPage + 1, detailPageSize, appliedDetailSearch)}
              >
                Next
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default MonthlyFaxReport;